import { useCallback, useEffect, useRef, useState } from 'react';

import Cookies from 'js-cookie';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ATLASSIAN_ACCOUNT_ID_COOKIE_KEY } from '@/feature-gates/bootstrapper';
import { updateFeatureGatesClientWithUserIdentifiers } from '@/feature-gates/initialization.tsx';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href.ts';
import { experiments } from '@/utils/experiment/manifest.ts';
import { EnrollmentType, useExperiment } from '@/utils/experiment/use-experiment.ts';
import { getWacIntegrationUrl } from '@/utils/urls.ts';

// sessionStorage flag marking that this session has already been offered the
// Loom Land opt-in choice. It guards against an infinite redirect loop: the
// /loom/choose CTAs navigate back to /try/cloud/signup (bundle=loom or
// teamwork-collection), which re-mounts this surface — without the guard we
// would re-fire exposure and redirect to /loom/choose again. The guard also
// enforces the "fire exposure at most once per session" contract.
export const LOOM_LAND_CHOICE_OFFERED_KEY = 'loomLandChoiceOffered';

const hasChoiceBeenOffered = (): boolean => {
	try {
		return window.sessionStorage.getItem(LOOM_LAND_CHOICE_OFFERED_KEY) === 'true';
	} catch {
		// sessionStorage can throw (privacy mode / disabled storage). Treat an
		// inaccessible store as "not offered" and fail open so the experiment
		// still runs; the single-fire ref below still prevents double-firing
		// within a single page load.
		return false;
	}
};

const markChoiceOffered = (): void => {
	try {
		window.sessionStorage.setItem(LOOM_LAND_CHOICE_OFFERED_KEY, 'true');
	} catch {
		// Ignore — see hasChoiceBeenOffered.
	}
};

/**
 * Post-identity exposure + routing for the TWCG-2108 Loom Land opt-in
 * experiment, mounted on the logged-in Loom site-creation surface
 * (LoggedInLoomNewUser at /try/cloud/signup).
 *
 * This is the earliest point in the Loom bundle journey where the user's
 * identity is guaranteed, so exposure can be bucketed on `atlassianAccountId`
 * (stable, cross-session, server-joinable) rather than the anonymous
 * `analyticsAnonymousId` used by the previous pre-identity fire.
 *
 * Behaviour:
 *  1. Re-initialise the feature-gate client against the logged-in account
 *     (`updateUser` with `atlassianAccountId`). This is async;
 *     `exposureResolved` stays `false` until it resolves. Callers MUST render
 *     nothing while `!exposureResolved` so the exposure fires against the
 *     correct (post-identity) bucketing and no control/treatment UI flashes
 *     before a treatment redirect (A1 gating).
 *  2. Once resolved, read the resolved cohort and — unless this session has
 *     already been offered the choice — fire exposure exactly once. Treatment
 *     cohorts (`radio` / `longText`) redirect to /loom/choose before paint;
 *     `control` falls through to render the standard site-creation screen.
 *
 * Fails open: if the account id is missing or `updateUser` rejects,
 * `exposureResolved` still flips to `true` so the site-creation screen is never
 * hard-blocked.
 *
 * @returns `exposureResolved` — `true` once feature-gate re-keying has settled
 *   and exposure/routing has been handled, i.e. it is safe to paint the
 *   site-creation form because any treatment redirect has already been issued.
 */
export const useAccountAwareLoomExposure = (): { exposureResolved: boolean } => {
	const [exposureResolved, setExposureResolved] = useState(false);
	const updateCalledOnce = useRef(false);
	const exposureHandledOnce = useRef(false);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const loomLandOptIn = useExperiment(experiments.twcg2108LoomLandOptIn);
	const choosePath = useLocalizedHref('/try/cloud/loom/choose');

	const updateClientWithAccount = useCallback(async () => {
		if (typeof window !== 'undefined' && window.featureGateBootstrapValues) {
			const rawAtlassianAccountId = Cookies.get(ATLASSIAN_ACCOUNT_ID_COOKIE_KEY);
			// The account id cookie stores the AAID percent-encoded (the colon in
			// `<orgId>:<uuid>` is written as `%3A`), whereas the bootstrap identifier
			// is stored raw. Decode before comparing/using so we (a) don't spuriously
			// re-key when the ids are actually equal, and (b) never re-key the
			// feature-gate client with a malformed (`%3A`) account id — either of
			// which leaves the experiment on its default bucket and silently
			// suppresses exposure. Decoding is idempotent for already-decoded values.
			const atlassianAccountId = rawAtlassianAccountId
				? decodeURIComponent(rawAtlassianAccountId)
				: rawAtlassianAccountId;
			// Only re-key the client if we actually have an account id and it is
			// not already the bootstrapped identifier.
			if (
				atlassianAccountId &&
				window.featureGateBootstrapValues.identifiers.atlassianAccountId !== atlassianAccountId
			) {
				try {
					// Re-evaluate Statsig against the logged-in account. WAC bootstraps
					// the feature-gate client pre-login (keyed on analyticsAnonymousId),
					// but this experiment randomizes on atlassianAccountId — so we must
					// re-key once the account is known or the experiment stays on its
					// default bucket. `updateFeatureGatesClientWithUserIdentifiers` is a
					// thin wrapper over the standard `FeatureGates.updateUser` SDK call
					// (see feature-gates/initialization.tsx). This mirrors the existing
					// tenant-aware precedent, `useUpdateFeatureGatesClientWithTenantId` /
					// TenantAwareStatsigClientUpdaterProvider, which does the same dance
					// for tenantId post-provisioning.
					await updateFeatureGatesClientWithUserIdentifiers(window.featureGateBootstrapValues, {
						atlassianAccountId,
					});
				} catch (error) {
					createAnalyticsEvent({
						action: 'failed',
						actionSubject: 'AccountAwareLoomExposure.updateClientWithAccount',
						eventType: 'operational',
						attributes: {
							error: error instanceof Error ? error.message : '',
						},
					}).fire();
				}
			}
		}
		setExposureResolved(true);
	}, [createAnalyticsEvent]);

	// Step 1: re-key the feature-gate client against the logged-in account, once.
	useEffect(() => {
		if (!updateCalledOnce.current) {
			updateCalledOnce.current = true;
			updateClientWithAccount();
		}
	}, [updateClientWithAccount]);

	// Step 2: once the client is re-keyed and the arm has resolved, fire exposure
	// (at most once per session) and route treatment cohorts to /loom/choose.
	useEffect(() => {
		if (!exposureResolved || exposureHandledOnce.current) {
			return;
		}
		// Wait for a definitively resolved arm before firing so exposure reflects
		// the post-identity bucketing rather than the default `control` fallback.
		if (loomLandOptIn.type !== EnrollmentType.ENROLLED || loomLandOptIn.usingDefault !== false) {
			return;
		}

		exposureHandledOnce.current = true;

		if (hasChoiceBeenOffered()) {
			return;
		}

		loomLandOptIn.fireExposure();
		markChoiceOffered();

		if (loomLandOptIn.value !== 'control') {
			// Absolute WAC URL: this is a top-level navigation into the choose
			// screen. Forward the current query params so intent/bundle context is
			// preserved (matches the previous logged-out redirect).
			window.location.assign(`${getWacIntegrationUrl()}${choosePath}${window.location.search}`);
		}
	}, [exposureResolved, loomLandOptIn, choosePath]);

	return { exposureResolved };
};
